import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import {
    // AbstractControl,   // ID SOLO NUMERICO (disattivato)
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    // ValidationErrors,  // ID SOLO NUMERICO (disattivato)
    Validators
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, takeUntil } from 'rxjs';
import { EkButtonComponent } from '@beta-foundations/ui-kit/buttons';
import {
    EkDialogBodyComponent,
    EkDialogFooterComponent,
    EkDialogHeaderComponent
} from '@beta-foundations/ui-kit/overlay/dialog';
import { EkInputTextComponent } from '@beta-foundations/ui-kit/form/input-text';
import { EkSelectComponent } from '@beta-foundations/ui-kit/form/select';
import { SubtenantService } from '../../services/subtenant.service';
import { NotificationService } from '../../services/notification.service';
import { CreateSubtenantPayload, SubtenantsResponse, UpdateSubtenantPayload } from '../../models/insegnamento.model';

// ---------------------------------------------------------------------------
// ID SOLO NUMERICO - soluzione disattivata su richiesta: l'id torna ad accettare
// qualsiasi carattere. Per riattivarla togliere il commento a questo blocco, ai
// due import in cima al file, alla chiamata di showIdErrorWhileTyping() nel
// costruttore, al metodo stesso e al validatore in buildForm().
// La chiave di traduzione `validators.idNumber` e' gia' presente in it.json/en.json.
//
// /** L'id di un corso/sotto tenant e' sempre numerico: nessun altro carattere e' ammesso. */
// const NUMERIC_ID_PATTERN = /^[0-9]+$/;
//
// /**
//  * `ek-error-help` della ui-kit mostra, per ogni chiave d'errore del controllo, la
//  * traduzione di `validators.<chiave>`: la chiave `idNumber` corrisponde quindi a
//  * `validators.idNumber`. Il campo vuoto e' gestito da `required`.
//  */
// function numericIdValidator(control: AbstractControl): ValidationErrors | null {
//     const value = (control.value ?? '').toString().trim();
//     if (!value || NUMERIC_ID_PATTERN.test(value)) {
//         return null;
//     }
//     return { idNumber: true };
// }
// ---------------------------------------------------------------------------

export interface SubtenantEditData {
    id: string;
    name: string;
    degreeClass?: string;
    departmentName?: string;
    locationName?: string;
    address?: string;
    year?: string;
}

@Component({
    selector: 'app-create-subtenant-modal',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        EkButtonComponent,
        EkDialogHeaderComponent,
        EkDialogBodyComponent,
        EkDialogFooterComponent,
        EkInputTextComponent,
        EkSelectComponent
    ],
    templateUrl: './create-subtenant-modal.component.html',
    styleUrl: './create-subtenant-modal.component.scss'
})
export class CreateSubtenantModalComponent implements OnDestroy {

    private readonly destroy$ = new Subject<void>();

    subtenantForm: FormGroup;
    isLoading = false;
    isEditMode = false;
    yearOptions: { label: string; value: string }[] = [];

    private originalSubtenant: SubtenantEditData | null = null;

    constructor(
        private fb: FormBuilder,
        public ref: DynamicDialogRef,
        private config: DynamicDialogConfig,
        private subtenantService: SubtenantService,
        private notificationService: NotificationService,
        private translate: TranslateService
    ) {
        this.subtenantForm = this.buildForm();
        this.yearOptions = this.buildYearOptions();
        this.initEditMode();
        // ID SOLO NUMERICO (disattivato): this.showIdErrorWhileTyping();
    }

    // ID SOLO NUMERICO - soluzione disattivata su richiesta.
    // /**
    //  * `ek-error-help` mostra l'errore solo quando il controllo e' `touched`, cioe' dopo il
    //  * blur: marcarlo al primo input fa comparire il messaggio gia' mentre si digita.
    //  */
    // private showIdErrorWhileTyping(): void {
    //     const control = this.subtenantForm.get('id');
    //     if (!control) {
    //         return;
    //     }
    //     control.valueChanges
    //         .pipe(takeUntil(this.destroy$))
    //         .subscribe(() => {
    //             // `dirty` esclude le patch programmatiche dell'apertura in modifica.
    //             if (control.dirty && !control.touched) {
    //                 control.markAsTouched();
    //             }
    //         });
    // }

    private initEditMode(): void {
        const subtenant = this.config?.data?.subtenant as SubtenantEditData | undefined;
        if (!subtenant) {
            return;
        }
        this.isEditMode = true;
        this.originalSubtenant = subtenant;
        this.ensureYearOption(subtenant.year);
        this.subtenantForm.patchValue({
            id: subtenant.id ?? '',
            name: subtenant.name ?? '',
            degreeClass: subtenant.degreeClass ?? '',
            departmentName: subtenant.departmentName ?? '',
            locationName: subtenant.locationName ?? '',
            address: subtenant.address ?? '',
            year: (subtenant.year ?? '').toString().trim() || null
        });
    }

    private buildYearOptions(): { label: string; value: string }[] {
        const current = new Date().getFullYear();
        const options: { label: string; value: string }[] = [];
        for (let start = current + 12; start >= current - 16; start--) {
            const value = `${start}/${start + 1}`;
            options.push({ label: value, value });
        }
        return options;
    }


    private ensureYearOption(year: string | null | undefined): void {
        const value = (year ?? '').toString().trim();
        if (value && !this.yearOptions.some(option => option.value === value)) {
            this.yearOptions = [{ label: value, value }, ...this.yearOptions];
        }
    }

    get modalTitle(): string {
        return this.isEditMode ? 'INSEGNAMENTO.EDIT_MODAL.HEADER' : 'INSEGNAMENTO.CREATE_MODAL.HEADER';
    }

    get submitLabel(): string {
        return this.isEditMode ? 'INSEGNAMENTO.EDIT_MODAL.SAVE' : 'INSEGNAMENTO.CREATE_MODAL.CREA';
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get isFormValid(): boolean {
        return this.subtenantForm.valid && !this.isLoading;
    }

    getFieldErrorMessage(fieldName: string): string {
        const control = this.subtenantForm.get(fieldName);
        if (!control || !control.errors || !control.touched) {
            return '';
        }
        if (control.errors['required']) {
            return this.translate.instant('validators.required');
        }
        return '';
    }

    close(): void {
        this.ref?.close();
    }

    save(): void {
        if (!this.subtenantForm.valid || this.isLoading) {
            this.subtenantForm.markAllAsTouched();
            return;
        }

        if (this.isEditMode) {
            this.update();
        } else {
            this.create();
        }
    }

    private create(): void {
        this.isLoading = true;
        const payload = this.buildPayload();

        this.subtenantService.createSubtenant(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (created: SubtenantsResponse) => {
                    this.isLoading = false;
                    this.notificationService.success(
                        this.translate.instant('INSEGNAMENTO.CREATE_MODAL.SUCCESS', {
                            name: payload.name
                        })
                    );
                    this.ref?.close({ action: 'created', subtenant: created ?? payload });
                },
                error: (error) => {
                    this.isLoading = false;
                    this.notificationService.showBackendError(
                        error,
                        this.translate.instant('INSEGNAMENTO.CREATE_MODAL.ERROR')
                    );
                }
            });
    }

    private update(): void {
        this.isLoading = true;
        const payload = this.buildUpdatePayload();

        this.subtenantService.updateSubtenant(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (updated: SubtenantsResponse) => {
                    this.isLoading = false;
                    this.notificationService.success(
                        this.translate.instant('INSEGNAMENTO.EDIT_MODAL.SUCCESS', {
                            name: payload.name
                        })
                    );
                    this.ref?.close({ action: 'updated', subtenant: updated ?? payload });
                },
                error: (error) => {
                    this.isLoading = false;
                    this.notificationService.showBackendError(
                        error,
                        this.translate.instant('INSEGNAMENTO.EDIT_MODAL.ERROR')
                    );
                }
            });
    }

    private buildPayload(): CreateSubtenantPayload {
        const value = this.subtenantForm.getRawValue();
        const trim = (input: string): string => (input ?? '').trim();
        return {
            id: trim(value.id),
            name: trim(value.name),
            customAttributes: {
                degreeClass: trim(value.degreeClass),
                departmentName: trim(value.departmentName),
                locationName: trim(value.locationName),
                address: trim(value.address),
                year: trim(value.year)
            }
        };
    }

    private buildUpdatePayload(): UpdateSubtenantPayload {
        const value = this.subtenantForm.getRawValue();
        const trim = (input: string): string => (input ?? '').trim();
        const original = this.originalSubtenant!;

        return {
            id: trim(value.id),
            name: trim(value.name),
            originalId: String(original.id ?? ''),
            originalName: String(original.name ?? ''),
            customAttributes: {
                degreeClass: trim(value.degreeClass),
                departmentName: trim(value.departmentName),
                locationName: trim(value.locationName),
                address: trim(value.address),
                year: trim(value.year)
            }
        };
    }

    private buildForm(): FormGroup {
        return this.fb.group({
            // ID SOLO NUMERICO (disattivato): id: ['', [Validators.required, numericIdValidator]],
            id: ['', [Validators.required]],
            name: ['', [Validators.required]],
            degreeClass: [''],
            departmentName: [''],
            locationName: [''],
            address: [''],
            year: [null]
        });
    }
}
